/**
 * Public RSVP page for events (birthday parties, open days, seminars).
 *
 * URL: /rsvp/:token — token is the event's public_share_token. No auth.
 * On submit we POST to /api/public/events/rsvp/:token; the server creates
 * an event_booking AND (if convert_guests_to_leads is on) a trial_lead,
 * then fires the org's drip sequences.
 *
 * Designed to work on a phone — shared via WhatsApp / iMessage / etc.
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';

const T = {
  bg: '#0a0d16', card: '#121724', text: '#ffffff', muted: '#8f9bb3',
  dim: '#5a6379', pri: '#5b6cf0', accent: '#22d3ee', ok: '#10b981',
  err: '#ef4444', brd: '#1e2538', el: '#161b24',
};

const css = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', -apple-system, sans-serif; background: ${T.bg}; color: ${T.text}; }
  input, textarea, select { width: 100%; padding: 12px 14px; border-radius: 10px;
    border: 1px solid ${T.brd}; background: ${T.el}; color: ${T.text};
    font-size: 15px; font-family: inherit; outline: none; }
  input:focus, textarea:focus { border-color: ${T.pri}; }
  label { display: block; font-size: 12px; color: ${T.muted}; margin-bottom: 5px; font-weight: 500; }
  .row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  @media (max-width: 500px) { .row { grid-template-columns: 1fr; } }
  .btn { width: 100%; padding: 14px; border-radius: 10px; border: none;
    background: ${T.pri}; color: #fff; font-size: 16px; font-weight: 600;
    cursor: pointer; }
  .btn:disabled { opacity: 0.5; cursor: default; }
  .field { margin-bottom: 14px; }
`;

export default function RsvpPage() {
  const { token } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const [form, setForm] = useState({
    guestName: '', guestEmail: '', guestPhone: '',
    childName: '', childAge: '', quantity: 1,
    dietaryNotes: '', message: '',
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/public/events/rsvp/${token}`);
        setEvent(data);
      } catch (err) {
        setError(err.response?.data?.error || 'Event not found');
      } finally { setLoading(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/public/events/rsvp/${token}`, form);
      setSubmitted(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally { setBusy(false); }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: T.bg, color: T.muted }}>
      Loading…
    </div>
  );

  if (error && !event) return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: T.bg, padding: 24 }}>
      <div style={{ textAlign: 'center', color: T.muted, maxWidth: 420 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>😕</div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 8 }}>Event not found</h1>
        <p>This RSVP link isn't valid. Please check with the host for an updated link.</p>
      </div>
    </div>
  );

  // Link back to the operator's branded marketing site. Both pages live on the
  // same origin (phoenixsuite.com.au) so a relative URL is enough — using
  // window.location.origin keeps it explicit across dev/preview environments.
  const orgSiteUrl = event?.orgSlug ? `/${event.orgSlug}` : null;

  const startDate = event?.startDate ? new Date(event.startDate).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : null;
  const eventTitle = event?.hostName ? `${event.hostName}'s ${event.name}` : event?.name;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '20px 16px 40px' }}>
      <Helmet>
        <title>RSVP — {eventTitle || 'Event'}</title>
        <meta name="description" content={`You're invited to ${eventTitle}${startDate ? ` on ${startDate}` : ''}.`} />
      </Helmet>
      <style>{css}</style>

      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        {event?.imageUrl && (
          <img src={event.imageUrl} alt="" style={{ width: '100%', borderRadius: 16, marginBottom: 20, maxHeight: 260, objectFit: 'cover' }} />
        )}

        <div style={{ background: T.card, border: `1px solid ${T.brd}`, borderRadius: 16, padding: 24, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.accent, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
            You're invited 🎉
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 10, letterSpacing: -0.5 }}>{eventTitle}</h1>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: event.description ? 16 : 0 }}>
            {startDate && <InfoRow icon="📅" text={startDate} />}
            {event.startTime && <InfoRow icon="🕐" text={`${String(event.startTime).slice(0, 5)}${event.endTime ? ' – ' + String(event.endTime).slice(0, 5) : ''}`} />}
            {event.location && <InfoRow icon="📍" text={event.location} />}
            {event.price > 0 && <InfoRow icon="💳" text={`$${event.price.toFixed(2)} per person`} />}
            {event.spotsLeft != null && event.spotsLeft > 0 && event.spotsLeft <= 10 &&
              <InfoRow icon="🎟️" text={`Only ${event.spotsLeft} spot${event.spotsLeft === 1 ? '' : 's'} left`} />}
            <InfoRow
              icon="🏢"
              text={orgSiteUrl
                ? <>Hosted by <a href={orgSiteUrl} style={{ color: T.accent, textDecoration: 'underline' }}>{event.orgName}</a></>
                : `Hosted by ${event.orgName}`}
            />
          </div>

          {event.description && (
            <p style={{ color: T.muted, fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {event.description}
            </p>
          )}
          {event.rsvpMessage && (
            <p style={{ color: T.text, fontSize: 14, lineHeight: 1.6, marginTop: 14, padding: 14, background: T.el, borderRadius: 10, whiteSpace: 'pre-wrap' }}>
              {event.rsvpMessage}
            </p>
          )}
        </div>

        {submitted ? (
          <div style={{ background: T.card, border: `1px solid ${T.ok}40`, borderRadius: 16, padding: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 52, marginBottom: 10 }}>🎉</div>
            <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>You're in!</h2>
            <p style={{ color: T.muted, fontSize: 14, lineHeight: 1.6 }}>{submitted.confirmation}</p>
            <p style={{ color: T.dim, fontSize: 12, marginTop: 14 }}>
              The host will be in touch closer to the date with any last-minute details.
            </p>
            {orgSiteUrl && (
              <a href={orgSiteUrl} className="btn" style={{ display: 'inline-block', marginTop: 18, padding: '12px 28px', borderRadius: 10, background: T.pri, color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
                Visit {event.orgName}
              </a>
            )}
          </div>
        ) : event.full ? (
          <div style={{ background: T.card, border: `1px solid ${T.brd}`, borderRadius: 16, padding: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 42, marginBottom: 10 }}>😔</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Sorry, this event is full</h2>
            <p style={{ color: T.muted, fontSize: 14 }}>Get in touch with the host about the waitlist.</p>
            {orgSiteUrl && (
              <a href={orgSiteUrl} style={{ display: 'inline-block', marginTop: 14, color: T.accent, fontSize: 13, textDecoration: 'underline' }}>
                Visit {event.orgName}
              </a>
            )}
          </div>
        ) : event.closed ? (
          <div style={{ background: T.card, border: `1px solid ${T.brd}`, borderRadius: 16, padding: 24, textAlign: 'center' }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>RSVPs are closed</h2>
            <p style={{ color: T.muted, fontSize: 14 }}>This event is no longer accepting RSVPs.</p>
            {orgSiteUrl && (
              <a href={orgSiteUrl} style={{ display: 'inline-block', marginTop: 14, color: T.accent, fontSize: 13, textDecoration: 'underline' }}>
                Visit {event.orgName}
              </a>
            )}
          </div>
        ) : (
          <form onSubmit={submit} style={{ background: T.card, border: `1px solid ${T.brd}`, borderRadius: 16, padding: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Your RSVP</h3>

            <div className="field">
              <label>Your name *</label>
              <input required value={form.guestName} onChange={(e) => setForm({ ...form, guestName: e.target.value })} placeholder="Sarah Johnson" />
            </div>
            <div className="field row">
              <div>
                <label>Email *</label>
                <input required type="email" value={form.guestEmail} onChange={(e) => setForm({ ...form, guestEmail: e.target.value })} placeholder="you@example.com" />
              </div>
              <div>
                <label>Phone</label>
                <input type="tel" value={form.guestPhone} onChange={(e) => setForm({ ...form, guestPhone: e.target.value })} placeholder="0400 000 000" />
              </div>
            </div>

            {event.eventType === 'social' && (
              <>
                <div className="field row">
                  <div>
                    <label>Child's name</label>
                    <input value={form.childName} onChange={(e) => setForm({ ...form, childName: e.target.value })} placeholder="Ella" />
                  </div>
                  <div>
                    <label>Child's age</label>
                    <input type="number" min="0" max="18" value={form.childAge} onChange={(e) => setForm({ ...form, childAge: e.target.value })} placeholder="7" />
                  </div>
                </div>
                <div className="field">
                  <label>Dietary notes or allergies</label>
                  <input value={form.dietaryNotes} onChange={(e) => setForm({ ...form, dietaryNotes: e.target.value })} placeholder="Nut allergy, vegetarian, etc." />
                </div>
              </>
            )}

            <div className="field">
              <label>How many people?</label>
              <select value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value) })}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <option key={n} value={n}>{n} {n === 1 ? 'person' : 'people'}</option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>Anything to share with the host? (optional)</label>
              <textarea rows="3" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Excited to see you there!" style={{ resize: 'vertical' }} />
            </div>

            {error && <div style={{ padding: 12, background: `${T.err}15`, color: T.err, borderRadius: 10, fontSize: 13, marginBottom: 12 }}>{error}</div>}

            <button type="submit" className="btn" disabled={busy}>{busy ? 'Sending…' : 'RSVP now →'}</button>

            <p style={{ fontSize: 11, color: T.dim, marginTop: 14, textAlign: 'center' }}>
              By RSVPing you agree to {event.orgName} contacting you about this event.
              <br />Your details stay private.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function InfoRow({ icon, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: T.text }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span>{text}</span>
    </div>
  );
}
